import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { AlertTriangle, Clock } from 'lucide-react';

const INACTIVITY_TIMEOUT = 30 * 60 * 1000; // 30 minutes in milliseconds
const WARNING_BEFORE_LOGOUT = 60 * 1000; // Show warning 60 seconds before logout

const InactivityHandler = ({ children }) => {
  const { user, logout } = useAuth();
  const [showWarning, setShowWarning] = useState(false);
  const [countdown, setCountdown] = useState(60);
  
  const timeoutRef = useRef(null);
  const warningTimeoutRef = useRef(null);
  const countdownRef = useRef(null);
  const lastActivityRef = useRef(Date.now());

  // Reset all timers and start fresh
  const resetTimers = useCallback(() => {
    lastActivityRef.current = Date.now();
    
    // Clear existing timers
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    if (warningTimeoutRef.current) clearTimeout(warningTimeoutRef.current);
    if (countdownRef.current) clearInterval(countdownRef.current);
    
    // Hide warning if it's showing
    if (showWarning) {
      setShowWarning(false);
      setCountdown(60);
    }

    // Only set timers if user is logged in
    if (!user) return;

    // Set warning timer (30 min - 60 sec = 29 min)
    warningTimeoutRef.current = setTimeout(() => {
      setShowWarning(true);
      setCountdown(60);
      
      // Start countdown
      countdownRef.current = setInterval(() => {
        setCountdown(prev => {
          if (prev <= 1) {
            clearInterval(countdownRef.current);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }, INACTIVITY_TIMEOUT - WARNING_BEFORE_LOGOUT);

    // Set logout timer (full 30 min)
    timeoutRef.current = setTimeout(() => {
      handleLogout();
    }, INACTIVITY_TIMEOUT);
  }, [user, showWarning]);

  // Handle logout
  const handleLogout = useCallback(() => {
    // Clear all timers
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    if (warningTimeoutRef.current) clearTimeout(warningTimeoutRef.current);
    if (countdownRef.current) clearInterval(countdownRef.current);
    
    setShowWarning(false);
    logout();
  }, [logout]);

  // Stay logged in - reset everything
  const handleStayLoggedIn = useCallback(() => {
    setShowWarning(false);
    setCountdown(60);
    resetTimers();
  }, [resetTimers]);

  // Track user activity
  useEffect(() => {
    if (!user) return;

    const activityEvents = ['mousedown', 'mousemove', 'keydown', 'scroll', 'touchstart', 'click'];
    
    const handleActivity = () => {
      // Only reset if warning is not showing (to prevent accidental resets)
      if (!showWarning) {
        resetTimers();
      }
    };

    // Throttle activity detection to avoid excessive resets
    let throttleTimer = null;
    const throttledActivity = () => {
      if (throttleTimer) return;
      throttleTimer = setTimeout(() => {
        handleActivity();
        throttleTimer = null;
      }, 1000); // Only register activity once per second
    };

    // Add event listeners
    activityEvents.forEach(event => {
      document.addEventListener(event, throttledActivity, { passive: true });
    });

    // Initialize timers
    resetTimers();

    // Cleanup
    return () => {
      activityEvents.forEach(event => {
        document.removeEventListener(event, throttledActivity);
      });
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      if (warningTimeoutRef.current) clearTimeout(warningTimeoutRef.current);
      if (countdownRef.current) clearInterval(countdownRef.current);
      if (throttleTimer) clearTimeout(throttleTimer);
    };
  }, [user, resetTimers, showWarning]);

  // Handle countdown reaching zero
  useEffect(() => {
    if (countdown === 0 && showWarning) {
      handleLogout();
    }
  }, [countdown, showWarning, handleLogout]);

  return (
    <>
      {children}
      
      {/* Inactivity Warning Modal */}
      {showWarning && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[9999]" data-testid="inactivity-warning">
          <div className="bg-white rounded-xl shadow-2xl p-6 max-w-md mx-4 animate-in fade-in zoom-in duration-200">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center">
                <AlertTriangle className="text-amber-600" size={24} />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-slate-900">Session Timeout Warning</h3>
                <p className="text-sm text-slate-500">You've been inactive for a while</p>
              </div>
            </div>
            
            <div className="bg-slate-50 rounded-lg p-4 mb-6">
              <div className="flex items-center justify-center gap-2 text-slate-700">
                <Clock size={20} />
                <span className="text-lg">
                  You'll be logged out in{' '}
                  <span className="font-bold text-amber-600">{countdown}</span>
                  {' '}seconds
                </span>
              </div>
            </div>
            
            <div className="flex gap-3">
              <button
                onClick={handleStayLoggedIn}
                className="flex-1 px-4 py-3 bg-indigo-600 text-white rounded-lg font-medium hover:bg-indigo-700 transition-colors"
                data-testid="stay-logged-in-btn"
              >
                Stay Logged In
              </button>
              <button
                onClick={handleLogout}
                className="px-4 py-3 border border-slate-300 text-slate-700 rounded-lg font-medium hover:bg-slate-50 transition-colors"
                data-testid="logout-now-btn"
              >
                Logout Now
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default InactivityHandler;
